import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    ADOwnerData,
    CustomOwnerData,
    OwnerFullData,
    OwnersAddedBy,
    ServiceAccount,
    ServiceAccountData,
    ServiceAccountEntity,
    ServiceAccountFilters,
    ServiceAccountInsight,
    ServiceAccountTab,
    SyncStatus
} from '../ServiceAccountsTypes';
import {
    HTTP_OPTIONS,
    INFINITE_SCROLL_BATCH_SIZE,
    PROTECTED_MODE,
    SERVICE_ACCOUNT_TYPES
} from '@silverfort-nodelibs/common/utils/consts';
import { compareValues } from '../utils/ServiceAccountsUtils';
import { RequiredModuleState } from '../../../../components/SystemSettings/hooks/lockedFeatures/types';
import { isAnyEnablingModulesActive } from '../../../../components/SystemSettings/hooks/lockedFeatures/utils';
import { usePricingModulesState, PricingModules } from '../../../../context/contexts/PricingModuleContext';
import { httpGet, httpPut } from '../../../../components/utils';
import { showAlert, SortDirection } from '@silverfort/sf-components';
import { RISK_LEVELS_COUNTER, SERVICE_ACCOUNT_INSIGHTS, SERVICE_ACCOUNT_TABS } from '../ServiceAccountsConsts';
import { showAlertError } from '../utils/ServiceAccountsUtils';

type ServiceAccountOrderBy = {
    column: keyof ServiceAccount;
    direction: SortDirection;
};

interface ServiceAccountContextProperties {
    setActiveFilters: Dispatch<SetStateAction<ServiceAccountFilters>>;
    fetchServiceAccounts: (resultsOffset: number) => void;
    selectServiceAccount: (serviceAccountGuid?: string) => void;
    setActiveTab: (tab: string) => void;
    setOrderBy: (orderBy: ServiceAccountOrderBy) => void;
    onServiceAccountUpdate: () => void;
    onServiceAccountUpdatePolicy: (guids: string[]) => void;
    setSelectedIds: (ids: number[]) => void;
    setServiceAccounts: (serviceAccounts: ServiceAccount[]) => void;
    clearSelectedIds: () => void;
    setShowAccountRemovedAlert: (shouldShow) => void;
    onRemove: (
        serviceAccounts: Pick<ServiceAccount, 'id' | 'guid' | 'is_protected' | 'display_name'>[],
        hide: boolean,
        discard: boolean,
        reason?: string,
        otherReason?: string,
        furtherDetails?: string
    ) => void;
    showAccountRemovedAlert: boolean;
    activeFilters: ServiceAccountFilters;
    serviceAccounts: ServiceAccount[];
    lastFetchLength: number;
    selectedServiceAccount?: ServiceAccountData;
    clearSelectedServiceAccount: () => void;
    updateServiceAccountData: (
        field: keyof ServiceAccountData,
        value:
            | string
            | string[]
            | boolean
            | number
            | ServiceAccountEntity[]
            | undefined
            | CustomOwnerData[]
            | ADOwnerData[],
        isProtectionChange?: boolean
    ) => void;
    selectedOwnersData: OwnerFullData[];
    serviceAccountFieldsToUpdate: Partial<ServiceAccountData>;
    updatedServiceAccount?: ServiceAccountData;
    tabs: { [key: string]: ServiceAccountTab };
    activeTab: string;
    orderBy: ServiceAccountOrderBy;
    insights: Record<string, ServiceAccountInsight>;
    riskLevelsCounter: Record<number, number>;
    isFetchingServiceAccounts: boolean;
    isFetchingServiceAccountData: boolean;
    isFetchingInsights: boolean;
    selectedServiceAccounts: ServiceAccount[];
    onPremNHILicenseProtectionEnabled: boolean;
    onPremNHILicenseITDREnabled: boolean;
    onPremNHILicenseISPMEnabled: boolean;
    setRulesSyncStatus: (syncStatus: SyncStatus) => void;
    rulesSyncStatus: SyncStatus;
}

const defaultServiceAccountContext: ServiceAccountContextProperties = {
    setActiveFilters: () => {},
    setSelectedIds: () => {},
    fetchServiceAccounts: () => {},
    selectServiceAccount: () => {},
    setActiveTab: () => {},
    setOrderBy: () => {},
    setRulesSyncStatus: () => {},
    onServiceAccountUpdate: () => {},
    onServiceAccountUpdatePolicy: () => {},
    setServiceAccounts: () => {},
    clearSelectedIds: () => {},
    setShowAccountRemovedAlert: () => {},
    onRemove: () => {},
    showAccountRemovedAlert: false,
    activeFilters: {},
    serviceAccounts: [],
    lastFetchLength: 0,
    selectedServiceAccount: undefined,
    clearSelectedServiceAccount: () => {},
    updateServiceAccountData: () => {},
    serviceAccountFieldsToUpdate: {},
    updatedServiceAccount: undefined,
    selectedOwnersData: [],
    tabs: {},
    activeTab: SERVICE_ACCOUNT_TYPES.MACHINE_TO_MACHINE.type,
    orderBy: { column: 'auths_count', direction: 'desc' },
    insights: {},
    riskLevelsCounter: {},
    isFetchingServiceAccounts: true,
    isFetchingServiceAccountData: false,
    isFetchingInsights: true,
    onPremNHILicenseProtectionEnabled: false,
    onPremNHILicenseITDREnabled: false,
    onPremNHILicenseISPMEnabled: false,
    selectedServiceAccounts: [],
    rulesSyncStatus: 'loading',
};

export const ServiceAccountContext = createContext<ServiceAccountContextProperties>(defaultServiceAccountContext);

export const ServiceAccountProvider = ({ children }) => {
    const [serviceAccounts, setServiceAccounts] = useState<ServiceAccount[]>([]);
    const [lastFetchLength, setLastFetchLength] = useState(0);
    const [activeFilters, setActiveFilters] = useState<ServiceAccountFilters>({});
    const [tabs, setTabs] = useState<{ [key: string]: ServiceAccountTab }>(SERVICE_ACCOUNT_TABS);
    const [activeTab, setActiveTab] = useState<string>(SERVICE_ACCOUNT_TYPES.ALL.type);
    const [orderBy, setOrderBy] = useState<ServiceAccountOrderBy>({ column: 'auths_count', direction: 'desc' });
    const [insights, setInsights] = useState<Record<string, ServiceAccountInsight>>(SERVICE_ACCOUNT_INSIGHTS);
    const [riskLevelsCounter, setRiskLevelsCounter] = useState<Record<string, number>>(RISK_LEVELS_COUNTER);
    const [showAccountRemovedAlert, setShowAccountRemovedAlert] = useState(false);

    const [isFetchingServiceAccounts, setIsFetchingServiceAccounts] = useState(true);
    const [isFetchingServiceAccountData, setIsFetchingServiceAccountData] = useState(false);
    const [isFetchingInsights, setIsFetchingInsights] = useState(true);

    const { pricingModulesState } = usePricingModulesState();
    const [onPremNHILicenseProtectionEnabled, setOnPremNHILicenseProtectionEnabled] = useState(false);
    const [onPremNHILicenseISPMEnabled, setOnPremNHILicenseISPMEnabled] = useState(false);
    const [onPremNHILicenseITDREnabled, setOnPremNHILicenseITDREnabled] = useState(false);

    const [rulesSyncStatus, setRulesSyncStatus] = useState<SyncStatus>('loading');

    useEffect(() => {
        if (!pricingModulesState) {
            return;
        }

        const nhiProtectionLicense = isAnyEnablingModulesActive(
            {
                [PricingModules.NHI_PROTECTION]: RequiredModuleState.ON_PREM_ENABLED
            },
            pricingModulesState
        );

        const itdrLicense = isAnyEnablingModulesActive(
            {
                [PricingModules.ITDR]: RequiredModuleState.ON_PREM_ENABLED
            },
            pricingModulesState
        );

        const ispmLicense = isAnyEnablingModulesActive(
            {
                [PricingModules.ISPM]: RequiredModuleState.ON_PREM_ENABLED
            },
            pricingModulesState
        );
        setOnPremNHILicenseISPMEnabled(ispmLicense);
        setOnPremNHILicenseITDREnabled(itdrLicense);
        setOnPremNHILicenseProtectionEnabled(nhiProtectionLicense);
    }, [pricingModulesState]);

    useEffect(() => {
        clearSelectedIds();
    }, [activeTab]);

    const selectedServiceAccounts = useMemo(() => {
        return Object.values(tabs).flatMap((tab) => tab.selectedServiceAccounts);
    }, [tabs]);

    const clearSelectedIds = useCallback(() => {
        setTabs((prevTabs) => {
            const oldTabs = prevTabs;
            Object.keys(prevTabs).map((tabType) => {
                return (oldTabs[tabType] = { ...prevTabs[tabType], selectedServiceAccounts: [] });
            });
            return {
                ...oldTabs
            };
        });
    }, []);

    const setSelectedIds = useCallback(
        (selectedIds: number[]) => {
            const currentTabSelectedSAs = serviceAccounts.filter(
                (serviceAccount) => selectedIds.includes(serviceAccount.id) && !serviceAccount.is_internal_sa
            );

            setTabs((prevTabs) => {
                const oldTabs = prevTabs;
                return {
                    ...oldTabs,
                    [activeTab]: { ...oldTabs[activeTab], selectedServiceAccounts: currentTabSelectedSAs }
                };
            });
        },
        [serviceAccounts, setTabs, activeTab]
    );

    const parsedActiveFilters = useMemo(() => {
        if (activeFilters.owner) {
            const owners = activeFilters.owner.map((ownerFullValue) => {
                const [guid] = ownerFullValue.split(',');
                return guid;
            });
            return { ...activeFilters, owner: owners };
        }
        if (activeFilters.smart_policies) {
            const smartPolicies = activeFilters.smart_policies.map((policyFullValue) => {
                const [id] = policyFullValue.split(',');
                return id;
            });
            return { ...activeFilters, smart_policies: smartPolicies };
        }
        return activeFilters;
    }, [activeFilters]);

    const fetchServiceAccounts = useCallback(
        async (resultsOffset = 0) => {
            const filtersWithType = {
                ...parsedActiveFilters,
                type_resolved: tabs[activeTab].type,
                showRemovedAccounts: tabs[activeTab].showRemovedAccounts
            };

            const params = {
                limitResults: INFINITE_SCROLL_BATCH_SIZE,
                resultsOffset,
                orderByField: orderBy.column,
                orderDirection: orderBy.direction,
                filters: filtersWithType
            };

            setIsFetchingServiceAccounts(true);
            try {
                const response: { data: { serviceAccountsData: ServiceAccount[] } | null } = await httpGet(
                    '/ServiceAccountsV2/all',
                    { [HTTP_OPTIONS.REQ_OPTIONS]: { params } }
                );

                const { serviceAccountsData = [] } = response.data || {};
                if (resultsOffset) {
                    setServiceAccounts((prev) => [...prev, ...serviceAccountsData]);
                } else {
                    setServiceAccounts(serviceAccountsData);
                }
                setLastFetchLength(serviceAccountsData.length);
            } catch (error) {
                console.error('failed fetching service accounts', error);
            }
            setIsFetchingServiceAccounts(false);
        },
        [parsedActiveFilters, activeTab, orderBy]
    );

    const fetchInsights = useCallback(async () => {
        setIsFetchingInsights(true);
        try {
            const response: {
                data: {
                    serviceAccountsInsights: { [insight: string]: number };
                    serviceAccountsRiskLevels: { risk: number; count: number }[];
                } | null;
            } = await httpGet('/ServiceAccountsV2/insights');
            setRiskLevelsCounter((prev) => {
                if (!response.data) {
                    return prev;
                }
                const newRiskLevelsCounter = { ...prev };
                response.data.serviceAccountsRiskLevels.forEach(({ risk, count }) => {
                    newRiskLevelsCounter[risk] = count;
                });
                return newRiskLevelsCounter;
            });
            setInsights((prev) => {
                if (!response.data) {
                    return prev;
                }

                const newInsights = { ...prev };
                for (const key in newInsights) {
                    newInsights[key].count = response.data.serviceAccountsInsights[key];
                }
                return newInsights;
            });
        } catch (error) {
            console.error('failed fetching insights', error);
        }
        setIsFetchingInsights(false);
    }, []);

    const fetchTabsData = useCallback(async () => {
        const params = { filters: parsedActiveFilters };

        try {
            const response: { data: { [key: string]: number } | null } = await httpGet(
                '/ServiceAccountsV2/amountPerType',
                { [HTTP_OPTIONS.REQ_OPTIONS]: { params } }
            );

            const amountByTab = response.data || {};
            setTabs((tabs) => {
                return Object.keys(tabs).reduce((newTabs, tabType) => {
                    newTabs[tabType] = { ...tabs[tabType], amount: amountByTab[tabType] || 0 };
                    return newTabs;
                }, {});
            });
        } catch (error) {
            console.error('failed fetching tabs data', error);
        }
    }, [parsedActiveFilters, setTabs]);

    const selectServiceAccount = useCallback(async (serviceAccountGuid?: string) => {
        if (!serviceAccountGuid) {
            setSelectedServiceAccount(undefined);
            return;
        }
        setIsFetchingServiceAccountData(true);
        const response: { data: ServiceAccountData | null } = await httpGet('/ServiceAccountsV2/extraData', {
            [HTTP_OPTIONS.REQ_OPTIONS]: { params: { serviceAccountGuid } },
            [HTTP_OPTIONS.HANDLE_ERRORS]: true
        });
        if (response.data) {
            setSelectedServiceAccount(response.data);
            setIsFetchingServiceAccountData(false);
        }
    }, []);

    const onServiceAccountUpdatePolicy = useCallback(
        (guids: string[]) => {
            fetchTabsData();
            fetchInsights();
            setServiceAccounts((prev) => {
                prev.filter((serviceAccount) => guids.includes(serviceAccount.guid)).forEach((serviceAccount) => {
                    serviceAccount.is_protected = true;
                    serviceAccount.protected_mode = PROTECTED_MODE.MANUAL;
                });
                return prev;
            });
        },
        [fetchTabsData, fetchInsights, setServiceAccounts]
    );

    const onServiceAccountUpdate = useCallback(() => {
        setRulesSyncStatus('loading')
        fetchTabsData();
        fetchInsights();
        fetchServiceAccounts();
    }, [fetchTabsData, fetchInsights, fetchServiceAccounts]);

    useEffect(() => {
        fetchTabsData();
    }, [fetchTabsData]);

    useEffect(() => {
        fetchServiceAccounts();
    }, [fetchServiceAccounts]);

    useEffect(() => {
        fetchInsights();
    }, [fetchInsights]);

    const onRemove = useCallback(
        async (
            serviceAccounts: Pick<ServiceAccount, 'id' | 'guid' | 'is_protected' | 'display_name'>[],
            hide: boolean,
            discard: boolean,
            reason?: string,
            otherReason?: string,
            furtherDetails?: string
        ) => {
            try {
                if (!serviceAccounts) {
                    showAlertError('discarding');
                    return;
                }
                const response: { data: { success: true; error: string } | null } = await httpPut(
                    '/ServiceAccountsV2/remove',
                    {
                        serviceAccounts: serviceAccounts.map((sa) => ({
                            id: sa.id,
                            guid: sa.guid,
                            display_name: sa.display_name,
                            is_protected: sa.is_protected
                        })),
                        isDisabled: discard,
                        isHidden: hide,
                        uiRemoveReason: reason,
                        customRemoveReason: otherReason,
                        furtherDetails
                    }
                );
                showAlert('Policies disabled for the selected accounts.', 'success');
                if (response.data?.error) {
                    showAlert('Operation failed or partially completed.', 'error');
                    return;
                }
                selectServiceAccount();
                onServiceAccountUpdate();
                setShowAccountRemovedAlert(true);
            } catch (err) {
                showAlert('Operation failed or partially completed.', 'error');
            }
        },
        [selectServiceAccount, onServiceAccountUpdate, setShowAccountRemovedAlert]
    );

    const [selectedServiceAccount, setSelectedServiceAccount] = useState<ServiceAccountData | undefined>();
    const [serviceAccountFieldsToUpdate, setServiceAccountFieldsToUpdate] = useState<Partial<ServiceAccountData>>({});
    const [selectedOwnersData, setSelectedOwnersData] = useState<OwnerFullData[]>([]);

    const clearSelectedServiceAccount = useCallback(() => {
        setSelectedServiceAccount(undefined);
        setServiceAccountFieldsToUpdate({});
    }, []);

    const updateServiceAccountData = useCallback(
        (
            field: keyof ServiceAccountData,
            value:
                | string
                | string[]
                | boolean
                | number
                | ServiceAccountEntity[]
                | undefined
                | CustomOwnerData[]
                | ADOwnerData[],
            isProtectionChange = false
        ) => {
            setServiceAccountFieldsToUpdate((prevState) => {
                const prevStateWithProtection = prevState;
                if (isProtectionChange) {
                    prevStateWithProtection.protected_mode = PROTECTED_MODE.MANUAL;
                }
                if (!compareValues(value, selectedServiceAccount?.[field])) {
                    return {
                        ...prevStateWithProtection,
                        [field]: value
                    };
                } else {
                    const { [field]: _, ...newState } = prevStateWithProtection;
                    return newState;
                }
            });
        },
        [selectedServiceAccount]
    );

    const updatedServiceAccount = useMemo(() => {
        if (selectedServiceAccount) {
            return {
                ...selectedServiceAccount,
                ...serviceAccountFieldsToUpdate
            };
        }
        return undefined;
    }, [selectedServiceAccount, serviceAccountFieldsToUpdate]);

    useEffect(() => {
        const fetchOwnersDataFromGuids = async () => {
            if (!updatedServiceAccount?.account_owners) return;
            const { data } = await httpGet('/ServiceAccountsV2/getServiceAccountOwnerData', {
                [HTTP_OPTIONS.REQ_OPTIONS]: {
                    params: {
                        guids: updatedServiceAccount.account_owners.map((owner) => owner.owner_guid)
                    }
                }
            });
            const ownersGuidsAddedBy: { [guid: string]: OwnersAddedBy } = {};
            updatedServiceAccount.account_owners.forEach((owner) => {
                ownersGuidsAddedBy[owner.owner_guid] = owner.added_by;
            });
            const ownersFullData: Omit<OwnerFullData, 'added_by'>[] = data || [];
            setSelectedOwnersData(
                ownersFullData.map((owner) => ({
                    ...owner,
                    added_by: ownersGuidsAddedBy[owner.guid]
                }))
            );
        };
        fetchOwnersDataFromGuids();
    }, [updatedServiceAccount?.account_owners]);

    return (
        <ServiceAccountContext.Provider
            value={{
                serviceAccounts,
                lastFetchLength,
                activeFilters,
                setActiveFilters,
                selectServiceAccount,
                fetchServiceAccounts,
                setActiveTab,
                onServiceAccountUpdate,
                activeTab,
                tabs,
                setOrderBy,
                orderBy,
                insights,
                riskLevelsCounter,
                isFetchingInsights,
                isFetchingServiceAccounts,
                isFetchingServiceAccountData,
                setSelectedIds,
                clearSelectedIds,
                setServiceAccounts,
                onServiceAccountUpdatePolicy,
                showAccountRemovedAlert,
                setShowAccountRemovedAlert,
                selectedServiceAccount,
                clearSelectedServiceAccount,
                updateServiceAccountData,
                serviceAccountFieldsToUpdate,
                updatedServiceAccount,
                selectedOwnersData,
                onRemove,
                onPremNHILicenseProtectionEnabled,
                onPremNHILicenseITDREnabled,
                onPremNHILicenseISPMEnabled,
                selectedServiceAccounts,
                rulesSyncStatus,
                setRulesSyncStatus
            }}>
            {children}
        </ServiceAccountContext.Provider>
    );
};

export const useServiceAccountContext = (): ServiceAccountContextProperties => useContext(ServiceAccountContext);
